/**
 * QuestionCard - Display questions with answers, scoring, effects
 */

import {
  HiOutlineQuestionMarkCircle,
  HiOutlineExclamationCircle,
  HiOutlineLightningBolt,
  HiOutlineChartBar,
} from 'react-icons/hi';
import { FaBrain, FaHeart, FaComments, FaBalanceScale } from 'react-icons/fa';

interface Answer {
  choice: string;
  text: string;
  description?: string;
  scoring?: {
    brain?: number;
    heart?: number;
    task?: number;
    comm?: number;
  };
  energyImpact?: string;
  consequences?: string;
}

interface QuestionCardProps {
  questionNumber: string;
  questionText: string;
  decisionType: string;
  answers: Answer[];
  context?: string;
  conditionalLogic?: string;
  stageNumber?: number;
}

const decisionTypeColors: Record<string, string> = {
  'Individual': 'bg-[#12356B]/10 text-[#12356B] border-[#CED4DE]',
  'Majority Vote': 'bg-[#12356B]/10 text-[#12356B] border-[#12356B]/25',
  'Consensus': 'bg-[#367A25]/10 text-[#367A25] border-[#367A25]/25',
  'Unanimous': 'bg-[#DD6832]/10 text-[#c4562a] border-[#DD6832]/30',
  'Isolated': 'bg-[#DD6832]/10 text-[#c4562a] border-[#DD6832]/30',
  'Conditional': 'bg-[#DD6832]/10 text-[#c4562a] border-[#DD6832]/30',
};

export function QuestionCard({
  questionNumber,
  questionText,
  decisionType,
  answers,
  context,
  conditionalLogic,
  stageNumber,
}: QuestionCardProps) {
  const decisionColorClass = decisionTypeColors[decisionType] || 'bg-[#E2E5EC] text-slate-800 border-[#CED4DE]';

  return (
    <div className="border border-[#CED4DE] rounded-lg p-6 bg-white shadow-sm mb-6">
      {/* Question Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3 flex-1">
          <HiOutlineQuestionMarkCircle className="text-2xl text-[#12356B] mt-1 flex-shrink-0" />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg font-bold text-[#12356B]">{questionNumber}</h3>
              {stageNumber !== undefined && (
                <span className="text-xs bg-[#E2E5EC] text-slate-600 px-2 py-1 rounded-full">Stage {stageNumber}</span>
              )}
            </div>
            <p className="text-sm text-slate-800 font-medium mb-3">{questionText}</p>
            {context && <p className="text-xs text-slate-600 italic mb-2">{context}</p>}
          </div>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${decisionColorClass}`}>
          {decisionType}
        </div>
      </div>

      {/* Conditional Logic Warning */}
      {conditionalLogic && (
        <div className="mb-4 p-3 bg-[#DD6832]/10 border border-[#DD6832]/30 rounded-lg flex items-start gap-2">
          <HiOutlineExclamationCircle className="text-[#c4562a] text-lg mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-xs font-semibold text-[#c4562a] mb-1">Conditional Logic</div>
            <div className="text-xs text-[#c4562a]">{conditionalLogic}</div>
          </div>
        </div>
      )}

      {/* Answer Choices */}
      <div className="space-y-4">
        {answers.map((answer) => (
          <div key={answer.choice} className="border border-[#CED4DE] rounded-lg p-4 bg-[#E2E5EC]/50">
            <div className="flex items-start gap-3">
              <div className="text-sm font-bold text-[#12356B] bg-[#12356B]/10 rounded-full w-7 h-7 flex items-center justify-center flex-shrink-0">
                {answer.choice}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-[#12356B] mb-2">{answer.text}</p>
                {answer.description && <p className="text-xs text-slate-600 mb-3">{answer.description}</p>}

                {/* Scoring */}
                {answer.scoring && (
                  <div className="flex items-center gap-4 mb-2 flex-wrap">
                    <div className="flex items-center gap-1 text-xs">
                      <HiOutlineChartBar className="text-slate-500" />
                      <span className="font-semibold text-slate-700">Scoring:</span>
                    </div>
                    {answer.scoring.brain !== undefined && (
                      <div className="flex items-center gap-1 text-xs">
                        <FaBrain className="text-[#12356B]" />
                        <span className="text-slate-700">
                          Brain: <span className="font-semibold">{answer.scoring.brain > 0 ? '+' : ''}{answer.scoring.brain}</span>
                        </span>
                      </div>
                    )}
                    {answer.scoring.heart !== undefined && (
                      <div className="flex items-center gap-1 text-xs">
                        <FaHeart className="text-[#c4562a]" />
                        <span className="text-slate-700">
                          Heart: <span className="font-semibold">{answer.scoring.heart > 0 ? '+' : ''}{answer.scoring.heart}</span>
                        </span>
                      </div>
                    )}
                    {answer.scoring.task !== undefined && (
                      <div className="flex items-center gap-1 text-xs">
                        <FaBalanceScale className="text-[#12356B]" />
                        <span className="text-slate-700">
                          Task: <span className="font-semibold">{answer.scoring.task > 0 ? '+' : ''}{answer.scoring.task}</span>
                        </span>
                      </div>
                    )}
                    {answer.scoring.comm !== undefined && (
                      <div className="flex items-center gap-1 text-xs">
                        <FaComments className="text-[#367A25]" />
                        <span className="text-slate-700">
                          Comm: <span className="font-semibold">{answer.scoring.comm > 0 ? '+' : ''}{answer.scoring.comm}</span>
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Energy Impact */}
                {answer.energyImpact && (
                  <div className="flex items-center gap-2 mb-2 text-xs">
                    <HiOutlineLightningBolt className="text-[#c4562a]" />
                    <span className="font-semibold text-slate-700">Energy:</span>
                    <span className="text-slate-700">{answer.energyImpact}</span>
                  </div>
                )}

                {/* Consequences */}
                {answer.consequences && (
                  <div className="mt-2 p-2 bg-[#E2E5EC] border border-[#CED4DE] rounded text-xs text-[#12356B]">
                    <span className="font-semibold">Consequence:</span> {answer.consequences}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
